import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useFormFactor } from '@/lib/formFactor';
import { Icon } from '@/components/Icon';
import { Tag } from '@/components/Tag';
import { Button } from '@/components/Button';
import { Alert, ErrorState, Skeleton } from '@/components/States';
import { money, num, dateTime, shortDate } from '@/lib/format';
import { documentLink, legacy, periodLabel, periodQuery, useGet, useStlPerms, type Period } from './api';
import { DATE_BASIS, EPS, LABELS, agingTone, basisLabel, cashState, documentInvoice, statementStatus } from './status';
import type { CashRecord, InvoiceRecord, Partner, Scope, StatementListItem, StatementPayload, SummaryPayload } from './types';
import { termsText } from './PartnerList';
import { DocList, docTotals } from './DocList';
import { RecordList, allocMap, toVM } from './RecordList';
import { StatementCreateDialog } from './StatementCreateDialog';
import { SettlementRecordDialog } from './SettlementRecordDialog';
import { AllocationPreviewDialog } from './AllocationPreviewDialog';
import { Kpis, PeriodBar, Section, bigMoney, type Kpi } from './ui';
import s from './PartnerDetail.module.css';

const unitAmount = (value: number, cents = false) => new Intl.NumberFormat('zh-CN', { minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: 2 }).format(value);

function MobileDocsTable({ docs, scope }: { docs: StatementPayload['businessDocuments']; scope: Scope }) {
  const totals = docTotals(docs);
  return <div className={s.phoneTableWrap}>
    <table className={s.phoneTable}>
      <thead><tr><th className={s.phoneSticky}>单据</th><th className={s.phoneRight}>金额</th><th className={s.phoneRight}>已核销</th><th className={s.phoneRight}>未核销</th><th>开票</th><th>类型</th></tr></thead>
      <tbody>{docs.map((doc) => {
        const invoiceState = documentInvoice(doc, scope);
        return <tr key={doc.lineId || doc.id}>
          <td className={s.phoneSticky}><b><DocLink doc={doc} /></b><small>{shortDate(doc.documentDate)}</small></td>
          <td className={s.phoneRight}>{doc.amount === undefined ? '—' : unitAmount(num(doc.amount), true)}</td>
          <td className={s.phoneRight}>{doc.cashAllocated === undefined ? '—' : unitAmount(num(doc.cashAllocated), true)}</td>
          <td className={`${s.phoneRight} ${num(doc.cashBalance) > EPS ? s.phoneOpen : ''}`}>{doc.cashBalance === undefined ? '—' : unitAmount(num(doc.cashBalance), true)}</td>
          <td><Tag tone={invoiceState.tone} sm>{invoiceState.label}</Tag></td>
          <td>{scope === 'customer' && doc.type === '业务订单' ? '销售订单' : doc.type}</td>
        </tr>;
      })}</tbody>
      <tfoot><tr><td className={s.phoneSticky}>合计 {docs.length} 张</td><td className={s.phoneRight}>{unitAmount(totals.amount, true)}</td><td className={s.phoneRight}>{unitAmount(totals.cashAllocated, true)}</td><td className={`${s.phoneRight} ${totals.cashBalance > EPS ? s.phoneOpen : ''}`}>{unitAmount(totals.cashBalance, true)}</td><td /><td /></tr></tfoot>
    </table>
  </div>;
}

function DocLink({ doc }: { doc: StatementPayload['businessDocuments'][number] }) {
  const link = documentLink(doc.targetType, doc.id);
  if (link.to) return <Link to={link.to}>{doc.documentNo}</Link>;
  if (link.href) return <a href={link.href}>{doc.documentNo}</a>;
  return <>{doc.documentNo}</>;
}

function MobileCashTable({ rows }: { rows: ReturnType<typeof toVM>[] }) {
  return <div className={s.phoneTableWrap}>
    <table className={s.phoneTable}>
      <thead><tr><th className={s.phoneSticky}>收款</th><th className={s.phoneRight}>金额</th><th className={s.phoneRight}>已核销</th><th className={s.phoneRight}>未核销</th><th>备注</th></tr></thead>
      <tbody>{rows.map((row) => <tr key={row.id}>
        <td className={s.phoneSticky}><b>{shortDate(row.occurredAt)}</b><small>{row.method || '—'}</small></td>
        <td className={s.phoneRight}>{row.amount === undefined ? '—' : unitAmount(num(row.amount), true)}</td>
        <td className={s.phoneRight}>{row.allocated === undefined ? '—' : unitAmount(num(row.allocated), true)}</td>
        <td className={`${s.phoneRight} ${num(row.unallocated) > EPS ? s.phoneOpen : ''}`}>{row.unallocated === undefined ? '—' : unitAmount(num(row.unallocated), true)}</td>
        <td>{row.note || <span className="em">—</span>}</td>
      </tr>)}</tbody>
    </table>
  </div>;
}

/**
 * 结算明细穿透（高保真稿 T3）：概要数字 → 业务单据 → 收付款台账 → 发票台账 → 对账单 → 结算设置。
 * 数据拼自四个只读接口：
 * - summary?customerId=…     这个单位在这段时间的应结 / 已结 / 应票 / 已票（需要「查看结算总览」）
 * - statements?partnerId=…   逐单核销明细（实时预览，只能按月；需要「查看对账单」）
 * - cash-records / invoice-records?customerId=…   台账（最多 200 条）
 * - statements?list=1&partnerId=…   这个单位保存过的对账单
 */
export function PartnerDetail({ scope, partnerId, period, basis, embedded, fullHref, onPeriod, onBasis }: {
  scope: Scope;
  partnerId: string;
  period: Period;
  basis: string;
  embedded?: boolean;
  /** iPad 分栏里「全屏查看」的地址 */
  fullHref?: string;
  onPeriod?: (p: Period) => void;
  onBasis?: (b: string) => void;
}) {
  const ff = useFormFactor();
  const phonePage = ff === 'phone' && !embedded;
  const desktopDetail = ff === 'desktop' && !embedded;
  const tabletDetail = ff === 'tablet' && !embedded;
  const wideDetail = desktopDetail || tabletDetail;
  const [actionParams, setActionParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(actionParams.get('action') === 'statement');
  const [recordOpen, setRecordOpen] = useState<'cash' | 'invoice' | null>(() => { const value = actionParams.get('action'); return value === 'cash' || value === 'invoice' ? value : null; });
  const [allocationOpen, setAllocationOpen] = useState(actionParams.get('action') === 'allocation');
  useEffect(() => { if (!actionParams.has('action')) return; const next = new URLSearchParams(actionParams); next.delete('action'); setActionParams(next, { replace: true }); }, [actionParams, setActionParams]);
  const [docsMode, setDocsMode] = useState<'cards' | 'table'>(() => {
    try { return localStorage.getItem('v2.settlements.partnerDetailMode') === 'table' ? 'table' : 'cards'; }
    catch { return 'cards'; }
  });
  const [phoneMoreOpen, setPhoneMoreOpen] = useState(false);
  const nav = useNavigate();
  const perms = useStlPerms();
  const L = LABELS[scope];
  const idKey = scope === 'supplier' ? 'supplierId' : 'customerId';
  const partnerType = scope === 'supplier' ? 'SUPPLIER' : 'CUSTOMER';
  const month = period.kind === 'month' ? period.value : undefined;

  const summary = useGet<SummaryPayload>(perms.overview ? '/api/v2/settlements/summary' : null, { scope, [idKey]: partnerId, ...periodQuery(period), dateBasis: basis });
  const preview = useGet<StatementPayload>(perms.statement && month ? '/api/settlements/statements' : null, { scope, partnerId, month, dateBasis: basis });
  const cash = useGet<CashRecord[]>('/api/settlements/cash-records', { partnerType, [idKey]: partnerId, ...periodQuery(period) });
  const invoice = useGet<InvoiceRecord[]>('/api/settlements/invoice-records', { partnerType, [idKey]: partnerId, ...periodQuery(period) });
  const saved = useGet<StatementListItem[]>(perms.statement ? '/api/settlements/statements' : null, { list: 1, scope, partnerId });

  const row = summary.data?.rows.find((r) => r.partnerId === partnerId);
  const terms = row || preview.data?.partner;
  // 这段时间没有任何往来时，汇总里不会有这一行：退回往来单位清单取名称
  const needName = !row && !preview.data && !summary.loading && !preview.loading;
  const partners = useGet<Partner[]>(needName ? '/api/settlements/partners' : null, { scope });
  const name = row?.partnerName || preview.data?.partner.name || partners.data?.find((p) => p.id === partnerId)?.name
    || (cash.data?.[0]?.customer || cash.data?.[0]?.supplier)?.name;
  const code = row?.partnerCode || preview.data?.partner.code || partners.data?.find((p) => p.id === partnerId)?.code;

  const goBack = () => (window.history.length > 1 ? nav(-1) : nav(`/settlements/${scope === 'supplier' ? 'suppliers' : 'customers'}`));
  // 加载中 / 失败也要留返回键：手机深层页没有 Tab 栏
  const bare = (body: ReactNode) => (
    <div className={`${s.page} ${embedded ? s.embedded : ''} ${ff === 'phone' && !embedded ? s.phone : ''}`}>
      {!embedded && (
        <header className={s.head}>
          <button type="button" className={s.back} onClick={goBack} aria-label="返回"><Icon name="back" size={20} /></button>
          <h1>{L.partner}结算明细</h1>
        </header>
      )}
      <div className={s.scroll}>{body}</div>
    </div>
  );
  if (!name && (summary.loading || preview.loading || cash.loading || partners.loading)) {
    return bare(<><Skeleton /><Skeleton lines={6} /></>);
  }
  if (!name && (summary.error || cash.error)) {
    const e = (summary.error || cash.error)!;
    return bare(<ErrorState title="结算明细加载失败" message={e.message} detail={`GET ${summary.error ? summary.url : cash.url}`} onRetry={() => { summary.reload(); cash.reload(); }} />);
  }

  const cs = cashState(row?.balanceAmount, scope);
  const phoneAge = row?.agingBucket !== '未到期' ? row?.agingBucket : null;
  const phoneStatus = phoneAge && cs?.label === L.open
    ? { label: phoneAge, tone: agingTone(phoneAge) || 'amber' }
    : cs;
  const balance = num(row?.balanceAmount);
  const amounts = row ? row.billAmount !== undefined : preview.data?.summary.billAmount !== undefined;
  const kpis: Kpi[] = !row
    ? []
    : amounts
      ? [
          { label: L.bill, value: bigMoney(row.billAmount) },
          { label: L.cashShort, value: bigMoney(row.cashAmount), tone: 'blue' },
          { label: balance < -EPS ? L.over : L.open, value: bigMoney(Math.abs(balance)), tone: balance > EPS ? 'bad' : balance < -EPS ? 'over' : 'mute' },
          { label: L.inv, value: bigMoney(row.invoiceAmount) },
          { label: num(row.invoiceBalanceAmount) < -EPS ? L.invOver : L.invOpen, value: bigMoney(Math.abs(num(row.invoiceBalanceAmount))), tone: num(row.invoiceBalanceAmount) > EPS ? 'warn' : 'mute' },
          { label: L.docs, value: `${row.orderCount} 张` },
        ]
      : [
          { label: L.docs, value: `${row.orderCount} 张` },
          { label: L.cashRecord, value: `${row.cashRecordCount} 笔` },
          { label: L.invRecord, value: `${row.invoiceRecordCount} 笔` },
        ];
  const phoneKpiLabels = scope === 'customer'
    ? ['业务金额', '已收', '未收', '已开票', '未开票', '单据']
    : ['采购金额', '已付', '未付', '已收票', '未收票', '单据'];
  const desktopKpiLabels = scope === 'customer'
    ? ['业务金额', '已收', '未收', '已开票', '未开票']
    : ['采购金额', '已付', '未付', '已收票', '未收票'];
  const displayKpis = phonePage && kpis.length === 6
    ? kpis.map((item, index) => ({ ...item, label: phoneKpiLabels[index] }))
    : wideDetail ? kpis.slice(0, 5).map((item, index) => ({ ...item, label: desktopKpiLabels[index], value: typeof item.value === 'string' ? item.value.replace(/^￥/, '') : item.value, sub: '元', tone: index === 2 && balance > EPS ? 'warn' as const : item.tone }))
      : kpis;

  const cashAlloc = allocMap(preview.data?.cashRecords);
  const invAlloc = allocMap(preview.data?.invoiceRecords);
  const cashRows = (cash.data ?? []).map((r) => toVM(r, cashAlloc));
  const invRows = (invoice.data ?? []).map((r) => toVM(r, invAlloc));
  const statements = saved.data ?? [];
  const thisMonthStatement = month ? statements.find((x) => x.settlementMonth === month && x.status !== 'CANCELLED') : undefined;
  const nextAllocation = preview.data?.businessDocuments.find((doc) => {
    const remaining = doc.cashBalance ?? (doc.amount === undefined ? 0 : num(doc.amount) - num(doc.cashAllocated));
    return remaining > EPS;
  });
  const nextInvoiceAllocations = (preview.data?.businessDocuments || []).flatMap((doc) => {
    if (!doc.invoiceRequired) return [];
    const remaining = doc.invoiceBalance ?? (doc.amount === undefined ? 0 : num(doc.amount) - num(doc.invoiceAllocated));
    return remaining > EPS ? [{ documentNo: doc.documentNo, amount: remaining }] : [];
  });
  const legacyStatement = legacy.statements({ scope, partnerId, month, dateBasis: basis });
  const phoneTableMode = phonePage && docsMode === 'table';
  const readOnly = import.meta.env.VITE_READONLY !== '0';
  const selectDocsMode = (value: 'cards' | 'table') => {
    setDocsMode(value);
    try { localStorage.setItem('v2.settlements.partnerDetailMode', value); } catch { /* preference is optional */ }
  };

  /* ---------------- 各段 ---------------- */
  const docsSection = (
    <Section
      flush
      title={<>{phonePage || wideDetail ? '业务单据' : L.docs}{preview.data && <small>{preview.data.businessDocuments.length} 张{phoneTableMode && ' · 单位：元'}</small>}</>}
      extra={<div className={s.docActions}>
        {wideDetail && preview.data && <span className={s.sortNote}>按单据日期升序，日期相同按单号排序——核销就是按这个顺序摊的。</span>}
        {phonePage && <div className={s.phoneMode} aria-label="业务单据显示模式">
          <button type="button" aria-label="卡片模式" aria-pressed={docsMode === 'cards'} className={docsMode === 'cards' ? s.phoneModeOn : ''} onClick={() => selectDocsMode('cards')}><Icon name="cards" size={14} /></button>
          <button type="button" aria-label="表格模式" aria-pressed={docsMode === 'table'} className={docsMode === 'table' ? s.phoneModeOn : ''} onClick={() => selectDocsMode('table')}><Icon name="table" size={14} /></button>
        </div>}
        {perms.allocation && month && !wideDetail && !phonePage && <Button size="sm" icon="ok" disabled={!preview.fresh || !preview.data} disabledReason="先加载本期单据和台账" onClick={() => setAllocationOpen(true)}>自动核销</Button>}
      </div>}
    >
      {!perms.statement ? (
        <p className={s.note}>需要「查看对账单」权限才能看到逐单的核销明细。</p>
      ) : !month ? (
        <p className={s.note}>{L.docs}的核销明细按月核对：把统计区间切到「按月」再看。</p>
      ) : preview.error ? (
        <ErrorState title={`${L.docs}加载失败`} message={preview.error.message} onRetry={preview.reload} />
      ) : !preview.data ? (
        <div className={s.pad}><Skeleton lines={3} /></div>
      ) : phoneTableMode ? (
        <><MobileDocsTable docs={preview.data.businessDocuments} scope={scope} /><p className={s.phoneTableFoot}>首列吸左，向左滑看「开票」「类型」。模式切换对整页生效，下次打开记住。</p></>
      ) : (
        <>
          <DocList docs={preview.data.businessDocuments} scope={scope} mode={phonePage ? docsMode : wideDetail ? 'compact' : 'auto'} compactDesktop={desktopDetail} showTotals={!wideDetail} empty={`${periodLabel(period)}没有${L.docs}（按${basisLabel(basis)}）。`} footerAction={phonePage && !phoneTableMode && perms.allocation ? <Button size="sm" icon="ok" disabled={!preview.fresh || !preview.data} disabledReason="先加载本期单据和台账" onClick={() => setAllocationOpen(true)}>自动核销</Button> : undefined} />
          {!wideDetail && <p className={s.foot}>按单据日期升序，日期相同按单号排序——自动核销就是按这个顺序摊的。</p>}
        </>
      )}
    </Section>
  );

  const recordSection = (kind: 'cash' | 'invoice') => {
    const res = kind === 'cash' ? cash : invoice;
    const rows = kind === 'cash' ? cashRows : invRows;
    const title = (phonePage || tabletDetail) && kind === 'cash' ? `${L.cashVerb}台账` : kind === 'cash' ? L.cashRecord : L.invRecord;
    const canWrite = kind === 'cash' ? perms.cashWrite(scope) : perms.invoiceWrite(scope);
    return (
      <Section
        flush
        title={<>{title}{res.data && <small>{rows.length} 笔{rows.some((r) => r.amount !== undefined) ? ` · ${money(rows.reduce((a, r) => a + num(r.amount), 0))}` : ''}</small>}</>}
        extra={canWrite && (phonePage && kind === 'cash' ? <button type="button" className={s.phoneTextAction} onClick={() => setRecordOpen(kind)}>登记{L.cashVerb}</button> : !phonePage && <Button size="sm" icon="plus" onClick={() => setRecordOpen(kind)}>登记{kind === 'cash' ? L.cashVerb : L.invVerb}</Button>)}
      >
        {res.error ? (
          <ErrorState title={`${title}加载失败`} message={res.error.message} onRetry={res.reload} />
        ) : !res.data ? (
          <div className={s.pad}><Skeleton lines={2} /></div>
        ) : phoneTableMode && kind === 'cash' ? (
          rows.length ? <MobileCashTable rows={rows} /> : <p className={s.note}>{periodLabel(period)}没有{title}。</p>
        ) : (
          <RecordList kind={kind} scope={scope} rows={rows} showPartner={false} mode={wideDetail && kind === 'cash' ? 'compact' : 'auto'} empty={`${periodLabel(period)}没有${title}。`} />
        )}
        {tabletDetail && kind === 'cash' && row && amounts && balance > EPS && (
          <p className={s.foot}>
            还有 {money(balance)} 未收，{terms?.settlementDay ? `约定每月 ${terms.settlementDay} 日结算${terms.paymentTermDays != null ? `，账期 ${terms.paymentTermDays} 天` : ''}。` : `${periodLabel(period)}按${basisLabel(basis)}统计。`}
          </p>
        )}
      </Section>
    );
  };

  const statementSection = perms.statement && (
    <Section
      flush
      title={<>对账单{statements.length > 0 && <small>{statements.length} 份</small>}</>}
      extra={perms.statementWrite && month && !thisMonthStatement && <button type="button" onClick={() => setCreateOpen(true)}>生成</button>}
    >
      <ul className={s.stl}>
        {month && !thisMonthStatement && saved.data && (
          <li className={s.stlNew}>
            <span className={s.stlIco}><Icon name="file" /></span>
            <div><b>{month} 对账单</b><small>本月还没生成{row ? ` · ${row.orderCount} 张${L.docs}` : ''}</small></div>
          </li>
        )}
        {statements.map((x) => {
          const st = statementStatus[x.status];
          return (
            <li key={x.id}>
              <Link to={`/settlements/statements/${x.id}`}>
                <span className={s.stlIco}><Icon name="receipt" /></span>
                <div>
                  <b>{x.settlementMonth} 对账单 <Tag tone={st.tone} sm>{st.label}</Tag></b>
                  <small>
                    {x.statementNo}
                    {x.billAmount !== undefined && ` · ${money(x.billAmount)}`}
                    {x.cashBalance !== undefined && Math.abs(num(x.cashBalance)) > EPS && ` · ${num(x.cashBalance) > 0 ? L.open : L.over} ${money(Math.abs(num(x.cashBalance)))}`}
                  </small>
                </div>
                <Icon name="chev" className={s.chev} />
              </Link>
            </li>
          );
        })}
        {saved.data && !statements.length && !month && <li className={s.stlNone}>还没有保存过对账单。</li>}
      </ul>
    </Section>
  );

  const settings = terms && (
    <Section title="结算设置">
      <dl className={s.kv}>
        <div><dt>结算方式</dt><dd>{terms.isMonthlySettlement ? '月结' : '非月结'}</dd></div>
        <div><dt>结算日</dt><dd>{terms.settlementDay ? `每月 ${terms.settlementDay} 日` : <span className="em">—</span>}</dd></div>
        <div><dt>账期</dt><dd>{terms.paymentTermDays != null ? `${terms.paymentTermDays} 天` : <span className="em">—</span>}</dd></div>
        <div><dt>日期口径</dt><dd>{basisLabel(basis)}</dd></div>
        {(wideDetail || phonePage) && scope === 'customer' && (row?.contact || row?.phone) && <div><dt>联系人</dt><dd>{[row.contact, row.phone].filter(Boolean).join(' · ')}</dd></div>}
      </dl>
    </Section>
  );

  const ageAmounts = row?.agingAmounts;
  const ageRows = ageAmounts ? [
    { label: '未到期', amount: ageAmounts.notDue, tone: s.ageCurrent },
    { label: '0–30 天', amount: ageAmounts.days0to30, tone: s.ageRecent },
    { label: '31–60 天', amount: ageAmounts.days31to60, tone: s.ageWarn },
    { label: '61–90 天', amount: ageAmounts.days61to90, tone: s.ageLate },
    { label: '90 天以上', amount: ageAmounts.over90, tone: s.ageSevere },
  ] : [];
  const ageTotal = ageRows.reduce((sum, item) => sum + item.amount, 0);
  const agingSection = amounts && row && (
    <Section title={<>账龄分布 <small>逐单未结 {money(ageTotal)}</small></>}>
      {ageRows.length ? <>
        {!(ff === 'desktop' && !embedded) && <div className={s.ageBar} aria-hidden="true">{ageRows.map((item) => item.amount > 0 && <span key={item.label} className={item.tone} style={{ width: `${item.amount / ageTotal * 100}%` }} />)}</div>}
        <div className={s.ageRows}>{ageRows.map((item) => <div key={item.label}><i className={item.tone} /><span>{item.label}</span><b className="tnum">{money(item.amount)}</b></div>)}</div>
      </> : <p className={s.ageNone}>这段时间没有未结且有到期日的单据。</p>}
      {row.undatedOrderCount ? <p className={s.ageNote}>另有 {row.undatedOrderCount} 张未结单据缺到期日，未计入账龄。</p> : null}
      {summary.data?.agingAsOf && <p className={`${s.ageNote} ${s.ageAsOf}`}>截至 {dateTime(summary.data.agingAsOf)} 的当前核销状态</p>}
    </Section>
  );

  const alert = row && amounts && (balance > EPS ? (
    <Alert tone="info" icon="yuan" title={`还有 ${money(balance)} ${L.open === '待收' ? '未收' : '未付'}`}>
      {row.overdueOrderCount ? `其中 ${row.overdueOrderCount} 张已逾期，逐单未结 ${money(row.overdueAmount)}。` : terms?.settlementDay ? `约定每月 ${terms.settlementDay} 日结算${terms.paymentTermDays != null ? `，账期 ${terms.paymentTermDays} 天` : ''}。` : `${periodLabel(period)}按${basisLabel(basis)}统计。`}
    </Alert>
  ) : balance < -EPS ? (
    <Alert tone="info" icon="yuan" title={`${L.over} ${money(-balance)}`}>{L.cash}超过了{L.bill}。</Alert>
  ) : null);

  const main = (
    <>
      {phoneTableMode && row && amounts ? <div className={s.phoneTableKpis}>
        <div><span>{scope === 'customer' ? '业务金额' : '采购金额'}</span><b>{unitAmount(num(row.billAmount))}</b><small>元</small></div>
        <div><span>{scope === 'customer' ? '未收' : '未付'}</span><b>{unitAmount(Math.abs(balance))}</b><small>元</small></div>
        <div><span>{scope === 'customer' ? '未开票' : '未收票'}</span><b>{unitAmount(Math.abs(num(row.invoiceBalanceAmount)))}</b><small>元</small></div>
      </div> : displayKpis.length > 0 ? <Kpis items={displayKpis} className={s.kpis} /> : summary.data && <p className={s.quiet}>{periodLabel(period)}没有{L.docs}，也没有{L.cashVerb}、{L.invVerb}记录。</p>}
      {phonePage && !phoneTableMode && alert}
      {docsSection}
      {recordSection('cash')}
      {!phonePage && !wideDetail && recordSection('invoice')}
      {!phonePage && !tabletDetail && alert}
    </>
  );
  const aside = (
    <>
      {phonePage ? <>
        {agingSection}
        {!phoneTableMode && settings}
        {!phoneTableMode && statementSection}
      </> : <>
        {settings}
        {agingSection}
        {statementSection}
      </>}
    </>
  );

  return (
    <div className={`${s.page} ${embedded ? s.embedded : ''} ${phonePage ? s.phone : ''} ${desktopDetail ? s.desktopDetail : ''}`}>
      <header className={`${s.head} ${phonePage ? s.phoneHead : ''}`}>
        {!embedded && (
          <button type="button" className={s.back} onClick={goBack} aria-label="返回">
            <Icon name="back" size={20} />
          </button>
        )}
        {phonePage ? <div className={s.phoneTitle}><h1>{phoneTableMode ? name || L.partner : '结算明细'}</h1><small>{phoneTableMode ? `结算明细 · ${period.kind === 'month' ? period.value : periodLabel(period)}` : `${periodLabel(period)} · ${basisLabel(basis, scope)}`}</small></div> : desktopDetail ? <div className={s.desktopTitleGroup}>
          <h1>{name || L.partner} · 结算明细</h1>
          <div className={s.desktopTitleMeta}><span>{terms && termsText(terms)}</span>{onPeriod && <div className={s.desktopPeriod}><PeriodBar period={period} onPeriod={onPeriod} basis={basis} basisOptions={DATE_BASIS[scope]} onBasis={onBasis} /></div>}</div>
        </div> : <>
          <h1>{name || L.partner}</h1>
          {tabletDetail && row?.agingBucket && <Tag tone={agingTone(row.agingBucket)}>{row.agingBucket}</Tag>}
          {cs && !wideDetail && <Tag tone={cs.tone}>{cs.label}</Tag>}
          <span className={s.sp} />
        </>}
        {embedded && fullHref && <Link className={s.open} to={fullHref}>全屏查看</Link>}
        {wideDetail && !readOnly && <div className={s.headActions}>
          {perms.invoiceWrite(scope) && <Button size="sm" icon="plus" onClick={() => setRecordOpen('invoice')}>登记开票</Button>}
          {perms.cashWrite(scope) && <Button size="sm" icon="plus" onClick={() => setRecordOpen('cash')}>登记收款</Button>}
          {perms.allocation && <Button size="sm" icon="ok" disabled={!month || !preview.fresh || !preview.data} disabledReason={!month ? '请先切换到按月查看' : '先加载本期单据和台账'} onClick={() => setAllocationOpen(true)}>自动核销</Button>}
          {perms.statementWrite && month && !thisMonthStatement && <Button size="sm" variant="primary" icon="plus" onClick={() => setCreateOpen(true)}>生成对账单</Button>}
        </div>}
        {phonePage ? <button type="button" className={s.phoneMore} aria-label="更多操作" aria-expanded={phoneMoreOpen} onClick={() => setPhoneMoreOpen((value) => !value)}><Icon name="more" size={20} /></button> : (!wideDetail || readOnly) && <a className={s.legacy} href={legacyStatement} title="只读体验期：登记、核销、生成对账单在老版操作">在老版中操作</a>}
      </header>
      <div className={s.scroll}>
        {phonePage && !phoneTableMode ? <div className={s.phonePartner}>
          <div className={s.phonePartnerName}><b>{name || L.partner}</b>{phoneStatus && <Tag tone={phoneStatus.tone}>{phoneStatus.label}</Tag>}</div>
          <p>{[code, terms && termsText(terms)].filter(Boolean).join(' · ')}</p>
        </div> : !phonePage && !desktopDetail && <p className={s.sub}>
          {[code, terms && termsText(terms), tabletDetail && periodLabel(period), tabletDetail && basisLabel(basis, scope)].filter(Boolean).join(' · ')}
          {embedded && <> · {periodLabel(period)} · 按{basisLabel(basis)}</>}
        </p>}
        {onPeriod && !phonePage && !tabletDetail && !desktopDetail && (
          <div className={s.pbar}>
            <PeriodBar period={period} onPeriod={onPeriod} basis={basis} basisOptions={DATE_BASIS[scope]} onBasis={onBasis} />
          </div>
        )}
        <div className={s.grid}>
          <div className={s.col}>{main}</div>
          <aside className={s.col}>{aside}</aside>
        </div>
        {!perms.overview && <p className={s.quiet}>没有「查看结算总览」权限，概要数字不显示。</p>}
        {!phonePage && statements.length > 0 && <p className={s.quiet}>最近一次对账单更新于 {dateTime(statements[0].updatedAt)}</p>}
      </div>
      {phonePage && (perms.cashWrite(scope) || perms.invoiceWrite(scope)) && (
        <footer className={`${s.bar} ${s.phoneActions}`}>
          <button type="button" className={s.phoneMoreBottom} aria-label="更多操作" aria-expanded={phoneMoreOpen} onClick={() => setPhoneMoreOpen((value) => !value)}><Icon name="more" size={20} /></button>
          {perms.invoiceWrite(scope) && <Button block onClick={() => setRecordOpen('invoice')}>登记{L.invVerb}</Button>}
          {perms.cashWrite(scope) && <Button block variant="primary" onClick={() => setRecordOpen('cash')}>登记{L.cashVerb}</Button>}
        </footer>
      )}
      {phonePage && phoneMoreOpen && <>
        <button type="button" className={s.phoneMoreScrim} aria-label="关闭更多操作" onClick={() => setPhoneMoreOpen(false)} />
        <div className={s.phoneMoreMenu} role="menu" aria-label="更多操作">
          <a href={legacyStatement} role="menuitem" title="只读体验期：登记、核销、生成对账单在老版操作">在老版中操作</a>
        </div>
      </>}
      {createOpen && month && <StatementCreateDialog scope={scope} partnerId={partnerId} month={month} dateBasis={basis} partnerName={name || L.partner} canConfirm={perms.confirm} onClose={() => setCreateOpen(false)} onDone={(id) => { setCreateOpen(false); saved.reload(); nav(`/settlements/statements/${id}`); }} />}
      {recordOpen && <SettlementRecordDialog kind={recordOpen} scope={scope} partnerId={partnerId} partnerName={name || L.partner} month={month} dateBasis={basis} suggestedAmount={recordOpen === 'cash' ? row?.balanceAmount : row?.invoiceBalanceAmount} suggestedAllocation={recordOpen === 'cash' && nextAllocation ? { documentNo: nextAllocation.documentNo, amount: nextAllocation.cashBalance === undefined ? Math.max(0, num(nextAllocation.amount) - num(nextAllocation.cashAllocated)) : num(nextAllocation.cashBalance) } : undefined} suggestedInvoiceAllocations={recordOpen === 'invoice' ? nextInvoiceAllocations : undefined} onClose={() => setRecordOpen(null)} onDone={() => { setRecordOpen(null); cash.reload(); invoice.reload(); summary.reload(); preview.reload(); }} />}
      {allocationOpen && preview.fresh && preview.data && <AllocationPreviewDialog data={preview.data} scope={scope} partnerId={partnerId} partnerName={name || L.partner} onDone={() => { setAllocationOpen(false); cash.reload(); invoice.reload(); summary.reload(); preview.reload(); }} onClose={() => setAllocationOpen(false)} />}
    </div>
  );
}
